import React from 'react';
import { Box } from '@mui/material';
import { CssTextField } from 'components/select';
import Autocomplete from '@mui/material/Autocomplete';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import throttle from 'lodash/throttle';
import { useRecoilState } from 'recoil';
import { OrderPayload, validOnboardingForm } from 'pages/project-onboarding/store';
import SearchIcon from '@mui/icons-material/Search';
import ProjectOnboardingStyles from 'pages/project-onboarding/style';
import AddMember from 'pages/project-onboarding/components/Form/ServiceMembers/AddMember';

const TestPlanRemoveUser = () => {
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [value, setValue] = React.useState(null);
    const [ccInputValue, setCCInputValue] = React.useState('-');
    const [options, setOptions] = React.useState([]);
    const [searched, setSearched] = React.useState(false);
    const [isValidForm, setValidForm] = useRecoilState(validOnboardingForm);

    const memberApi = useMutation(() => axios.get(`${endPoints.day2Operation?.ProjectOnboarding?.member}?key=${ccInputValue}`));
    const testPlanMemberPostApi = useMutation((payload) => axios.get(`${endPoints.day2Operation?.ProjectOnboarding?.member}?key=${payload}`));

    const setRemovedTestPlanUser = (removedTestPlanUser, removedTestPlanUserDetails) => {
        setOrder({
            ...order,
            removedTestPlanUser,
            removedTestPlanUserDetails,
        });
    };

    React.useEffect(() => {
        if (!order?.removedTestPlanUser && !order?.removedTestPlanUserDetails) {
            setOptions([]);
        }
    }, [order?.removedTestPlanUser, order?.removedTestPlanUserDetails]);

    const resetOptions = () => {
        setOptions([]);
        setRemovedTestPlanUser(null, null);
    };

    const fetch = React.useMemo(
        () =>
            throttle(() => {
                memberApi.mutate();
            }, 1000),
        []
    );
    const handleSearch = () => {
        // istanbul ignore if
        if (ccInputValue === '') {
            resetOptions();
        }
        // istanbul ignore else
        if (ccInputValue !== '' && ccInputValue.length > 2) {
            setSearched(true);
            fetch();
        }
    };

    const isExistsInOptions = (removedTestPlanUserValue) => options.find((option) => removedTestPlanUserValue === option.name);

    React.useEffect(() => {
        if (options && options?.length === 1 && !order?.removedTestPlanUser && order?.removedTestPlanUserDetails) {
            setOrder({ ...order, removedTestPlanUser: options[0]?.label, removedTestPlanUserDetails: options?.[0] });
        }
        if (options && options?.length > 1 && order?.removedTestPlanUserDetails && !order?.removedTestPlanUser) {
            const removedTestPlanUserObj = isExistsInOptions(order?.removedTestPlanUserDetails?.name);
            setOrder({ ...order, removedTestPlanUser: options[0]?.label, removedTestPlanUserDetails: removedTestPlanUserObj });
        }
    }, [options]);

    React.useEffect(() => {
        if (memberApi?.data?.data) {
            const combinedValue = memberApi?.data?.data?.map((app) => ({
                label: `${app?.gpid} - ${app?.name}`,
                gpid: `${app?.gpid}`,
                name: `${app?.name}`,
                email: app?.email,
            }));
            setOptions(combinedValue);
        }
    }, [memberApi?.data?.data]);

    React.useEffect(() => {
        if (testPlanMemberPostApi?.data?.data) {
            const combinedValue = testPlanMemberPostApi?.data?.data?.map((app) => ({
                label: `${app.gpid} - ${app.name}`,
                gpid: `${app.gpid}`,
                name: `${app.name}`,
                email: app.email,
            }));
            setOptions(combinedValue);
        }
    }, [testPlanMemberPostApi?.data?.data]);

    React.useEffect(() => {
        if (value === null) {
            // resetOptions();
        } else {
            setOptions(options?.filter((z) => z?.gpid === value?.gpid));
            setRemovedTestPlanUser(value?.label, value);
        }
    }, [value]);

    React.useEffect(() => {
        if (order?.removedTestPlanUser && !order?.removedTestPlanUserDetails) {
            testPlanMemberPostApi.mutate(order?.removedTestPlanUser);
        }
    }, [order?.removedTestPlanUser, order?.removedTestPlanUserDetails]);

    const validateMember = (member) => {
        const duplicate = [...order?.removedTestPlanUserList, ...order?.testPlanUserList]?.filter((item) => item?.gpid === member?.gpid);
        if (duplicate && duplicate?.length) {
            setValidForm({ ...isValidForm, isDuplicateRemovedTestPlanUser: true });
        } else {
            setValidForm({ ...isValidForm, isDuplicateRemovedTestPlanUser: false });
        }
    };

    const handleAddMember = () => {
        if (order?.removedTestPlanUserList?.length > 4) {
            setValidForm({ ...isValidForm, isRemovedTestPlanMaxMemberLimit: true });
        } else {
            setValidForm({ ...isValidForm, isRemovedTestPlanMaxMemberLimit: false });
            // eslint-disable-next-line
            if (!isValidForm?.isDuplicateRemovedTestPlanUser) {
                setOrder({
                    ...order,
                    removedTestPlanUserList: [...order.removedTestPlanUserList, order?.removedTestPlanUserDetails],
                    removedTestPlanUser: null,
                    removedTestPlanUserDetails: null,
                });
            }
        }
    };

    const getHelperText = () => {
        let errorText;
        if (isValidForm?.isDuplicateRemovedTestPlanUser) {
            errorText = 'Member already present in the Add Member or Remove Member list.';
        } else if (isValidForm?.isRemovedTestPlanMaxMemberLimit) {
            errorText = 'You have reached max limit of 5 members.';
        }
        return errorText;
    };

    const getNoOptionText = () => {
        let text = '';
        if (searched === false) {
            text = 'Type & press enter to search by GPID, Firstname and Lastname';
        } else if (options?.length === 0) {
            text = 'No results found';
        }
        return text;
    };

    return (
        <>
            {' '}
            <Box display="flex" alignItems="flex-start" gap={1}>
                <Autocomplete
                    key="member"
                    size="small"
                    noOptionsText={getNoOptionText()}
                    includeInputInList
                    loading={memberApi?.isLoading}
                    value={order?.removedTestPlanUser || null}
                    onInputChange={(event, newInputValue) => {
                        if (newInputValue) setCCInputValue(newInputValue?.includes('-') ? newInputValue?.split('-')?.pop()?.trim() : newInputValue);
                    }}
                    onChange={(event, newValue) => {
                        // istanbul ignore else
                        if (typeof newValue === 'string') {
                            setValue({
                                label: newValue,
                            });
                        } else if (newValue && newValue.inputValue) {
                            // Create a new value from the user input
                            setValue({
                                label: newValue.inputValue,
                            });
                        } else {
                            setValue(newValue);
                            validateMember(newValue);
                        }
                    }}
                    isOptionEqualToValue={(option, value1) => option.id === value1.id}
                    renderOption={(props, option) => (
                        <li {...props} key={option?.gpid}>
                            {option?.label}
                        </li>
                    )}
                    onBlur={() => {
                        if (!value || !order?.removedTestPlanUser) {
                            setSearched(false);
                            setOptions([]);
                        }
                    }}
                    onKeyUp={(event) => {
                        if (event.key === 'Enter') {
                            setSearched(true);
                            handleSearch();
                        }
                        if ((event.key === 'Backspace' || event.key === 'Delete') && (!event.target.value || event.target.value === '')) {
                            setSearched(false);
                            resetOptions();
                            setValidForm({ ...isValidForm, isDuplicateRemovedTestPlanUser: false, isRemovedTestPlanMaxMemberLimit: false });
                        }
                    }}
                    filterOptions={(optionValues, params) => {
                        // eslint-disable-next-line no-underscore-dangle
                        const _results = [];
                        // eslint-disable-next-line no-plusplus
                        for (let i = 0; i < optionValues.length; i++) {
                            // eslint-disable-next-line no-restricted-syntax
                            for (const key in optionValues[i]) {
                                if (optionValues[i][key] !== null && typeof optionValues[i][key] !== 'boolean' && typeof optionValues[i][key] !== 'number') {
                                    if (optionValues[i][key].toLowerCase().includes(params?.inputValue.toLowerCase())) {
                                        _results.push(optionValues[i]);
                                    }
                                }
                            }
                        }
                        return [...new Set(_results)];
                        // return _results
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    disableClearable
                    id="free-solo-with-text-demo"
                    options={options}
                    sx={{ width: '80%' }}
                    renderInput={(params) => (
                        <CssTextField
                            size="large"
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...params}
                            label="Test Plan Users *"
                            helperText={<span style={{ color: '#d40020' }}>{getHelperText()}</span>}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        <SearchIcon
                                            sx={ProjectOnboardingStyles.MemberAction.searchIcon}
                                            onClick={() => {
                                                handleSearch();
                                                setSearched(true);
                                            }}
                                            data-testid="search-btn"
                                        />
                                    </>
                                ),
                            }}
                            className={order?.isRemovedTestPlanMaxMemberLimit?.length === 0 && order?.isTouched ? 'validate' : ''}
                            fullWidth
                        />
                    )}
                />
                <AddMember handleAddMember={() => handleAddMember()} action="removeTestplanMembers" />
            </Box>
        </>
    );
};

export default React.memo(TestPlanRemoveUser);
